import { useState, useMemo, useEffect } from 'react';
import { loadPositions, savePositions, generateId, calcCushion, getStatus, pullFromCloud } from './storage';
import PositionModal from './PositionModal';
import TrailStopModal from './TrailStopModal';
import WeeklyReview from './WeeklyReview';
import { useCryptoFeed } from './useCryptoFeed';
import { useStockFeed } from './useStockFeed';

/* ── NLV persistence ── */
const NLV_KEY = 'isa_nlv';
function loadNlv() { try { return Number(localStorage.getItem(NLV_KEY)) || 0; } catch { return 0; } }
function saveNlv(v) { try { localStorage.setItem(NLV_KEY, String(v)); } catch {} }

/* ── Stale price: 7+ days since last price update ── */
function isPriceStale(p) {
  const ts = p.priceUpdatedAt ?? p.createdAt;
  if (!ts) return true;
  return (Date.now() - new Date(ts).getTime()) > 7 * 24 * 60 * 60 * 1000;
}

const STALE_TIP = 'Price not updated in 7+ days — click Edit to refresh';

export default function Dashboard() {
  const [positions,       setPositions]       = useState(() => { const p = loadPositions(); return Array.isArray(p) ? p : []; });
  const [addEditModal,    setAddEditModal]    = useState(null);
  const [trailModal,      setTrailModal]      = useState(null);
  const [showReview,      setShowReview]      = useState(false);
  const [nlv,             setNlv]             = useState(loadNlv);
  const [expandedNote,    setExpandedNote]    = useState(null);  // position id
  const [manualOverrides, setManualOverrides] = useState({});    // { [id]: number }
  const [editPrice,       setEditPrice]       = useState(null);  // { id, value } | null
  const [syncStatus,      setSyncStatus]      = useState(null);   // null | 'synced' | 'offline'

  /* ── Cloud sync: pull latest positions on mount ── */
  useEffect(() => {
    pullFromCloud().then(result => {
      if (!result) { setSyncStatus('offline'); return; }
      const local    = loadPositions();
      const cloudTs  = new Date(result.updatedAt).getTime();
      const localTs  = local.length > 0
        ? Math.max(...local.map(p => new Date(p.createdAt ?? 0).getTime()))
        : 0;
      // Use cloud data if it's newer or local is empty
      if (result.positions.length > 0 && (local.length === 0 || cloudTs > localTs)) {
        setPositions(Array.isArray(result.positions) ? result.positions : []);
        try { localStorage.setItem('isa_positions', JSON.stringify(result.positions)); } catch {}
      }
      setSyncStatus('synced');
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  /* ── Live crypto feed (CoinGecko, 60 s) ── */
  const { feedStatus, getLivePrice } = useCryptoFeed();

  /* ── Live stock / LETF feed (Yahoo Finance, 5 min) ── */
  const stockTickers = useMemo(
    () => positions
      .filter(p => p.assetClass === 'LETF' || p.assetClass === 'Stock' || p.assetClass === 'Future')
      .map(p => p.name.toUpperCase()),
    [positions]
  );
  const { getStockData } = useStockFeed(stockTickers);

  /* Resolve display price: manual override → live feed → stored value */
  function getEffectivePrice(p) {
    if (manualOverrides[p.id] != null) return manualOverrides[p.id];
    if (p.assetClass === 'Crypto') return getLivePrice(p.name) ?? Number(p.currentPrice);
    if (p.assetClass === 'LETF' || p.assetClass === 'Stock' || p.assetClass === 'Future') {
      return getStockData(p.name).price ?? Number(p.currentPrice);
    }
    return Number(p.currentPrice);
  }

  /* Commit an inline price override (on Enter or blur) */
  function commitOverride(id) {
    if (!editPrice || editPrice.id !== id) return;
    const val = Number(editPrice.value);
    if (val > 0) setManualOverrides(prev => ({ ...prev, [id]: val }));
    setEditPrice(null);
  }

  /* Discard manual override and return to live feed */
  function resumeLive(id) {
    setManualOverrides(prev => { const n = { ...prev }; delete n[id]; return n; });
  }

  function persist(updated) { setPositions(updated); savePositions(updated); }

  function handleSavePosition(data) {
    if (addEditModal.mode === 'add') {
      persist([...positions, {
        id: generateId(),
        stopHistory: [],
        createdAt: new Date().toISOString(),
        priceUpdatedAt: new Date().toISOString(),
        notes: '',
        ...data,
      }]);
    } else {
      persist(positions.map(p => {
        if (p.id !== data.id) return p;
        const priceChanged = Number(data.currentPrice) !== Number(p.currentPrice);
        return {
          ...p,
          name:         data.name,
          assetClass:   data.assetClass,
          shares:       data.shares,
          entryPrice:   data.entryPrice,
          currentPrice: data.currentPrice,
          notes:        data.notes ?? p.notes ?? '',
          ...(priceChanged ? { priceUpdatedAt: new Date().toISOString() } : {}),
        };
      }));
    }
    setAddEditModal(null);
  }

  function handleTrailStop(positionId, newStop) {
    persist(positions.map(p => {
      if (p.id !== positionId) return p;
      return {
        ...p,
        stopPrice: newStop,
        stopHistory: [...(p.stopHistory ?? []), { date: new Date().toISOString(), oldStop: p.stopPrice, newStop }],
      };
    }));
    setTrailModal(null);
  }

  function handleDelete(id) {
    if (window.confirm('Remove this position?')) persist(positions.filter(p => p.id !== id));
  }

  function handleNlvChange(e) {
    const v = Number(e.target.value) || 0;
    setNlv(v);
    saveNlv(v);
  }

  /* ── Portfolio summary calculations (use effective prices) ── */
  const totalPositionValue = positions.reduce((s, p) => s + p.shares * getEffectivePrice(p), 0);
  const totalCostBasis     = positions.reduce((s, p) => s + p.shares * (p.entryPrice ?? 0), 0);
  const totalPnl           = totalPositionValue - totalCostBasis;
  const totalPnlPct        = totalCostBasis > 0 ? (totalPnl / totalCostBasis) * 100 : 0;
  const cashValue          = Math.max(0, nlv - totalPositionValue);
  const base               = nlv > 0 ? nlv : totalPositionValue;

  const byClass = positions.reduce((acc, p) => {
    const cls = p.assetClass ?? 'Other';
    acc[cls] = (acc[cls] ?? 0) + p.shares * getEffectivePrice(p);
    return acc;
  }, {});

  const allocRows = [
    { label: 'LETF',   value: byClass.LETF   ?? 0, cls: 'letf'   },
    { label: 'Stocks', value: byClass.Stock  ?? 0, cls: 'stock'  },
    { label: 'Crypto', value: byClass.Crypto ?? 0, cls: 'crypto' },
    { label: 'Future', value: byClass.Future ?? 0, cls: 'future' },
    { label: 'Cash',   value: cashValue,            cls: 'cash'   },
  ].filter(r => r.value > 0);

  const counts = positions.reduce(
    (acc, p) => { const s = getStatus(calcCushion(getEffectivePrice(p), p.stopPrice)); acc[s]++; return acc; },
    { GREEN: 0, AMBER: 0, RED: 0 }
  );

  return (
    <div className="dashboard">

      {/* ══ Summary bar (quick stats + actions) ══ */}
      <div className="summary-bar">
        <div className="summary-group">
          <div className="summary-stat">
            <span className="summary-num">{positions.length}</span>
            <span className="summary-label">Positions</span>
          </div>
          <div className="summary-stat summary-green">
            <span className="summary-num">{counts.GREEN}</span>
            <span className="summary-label">Safe</span>
          </div>
          <div className="summary-stat summary-amber">
            <span className="summary-num">{counts.AMBER}</span>
            <span className="summary-label">Caution</span>
          </div>
          <div className="summary-stat summary-red">
            <span className="summary-num">{counts.RED}</span>
            <span className="summary-label">At Risk</span>
          </div>
        </div>
        <div className="summary-actions">
          {syncStatus && (
            <span style={{
              fontSize: '0.65rem', color: syncStatus === 'synced' ? '#34d399' : 'rgba(255,255,255,0.3)',
              display: 'flex', alignItems: 'center', gap: '4px'
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
              {syncStatus === 'synced' ? 'Synced' : 'Offline'}
            </span>
          )}
          <button className="btn btn-ghost dash-btn-sm" onClick={() => setShowReview(true)}>
            📋 Weekly Review
          </button>
          <button className="btn btn-primary dash-btn-sm" onClick={() => setAddEditModal({ mode: 'add' })}>
            + Add Position
          </button>
        </div>
      </div>

      {/* ══ Portfolio Summary Card ══ */}
      <div className="portfolio-card">
        <div className="pc-header">
          <span className="pc-title">Portfolio Summary</span>
        </div>

        {/* NLV input */}
        <div className="pc-nlv-row">
          <label className="pc-nlv-label">Net Liquidation Value</label>
          <div className="pc-nlv-field">
            <span className="pc-nlv-sym">$</span>
            <input
              type="number"
              className="form-input pc-nlv-input"
              min="0"
              step="100"
              value={nlv || ''}
              onChange={handleNlvChange}
              placeholder="Enter total portfolio value"
            />
          </div>
        </div>

        {/* Three metric columns */}
        <div className="pc-metrics">

          {/* Allocation */}
          <div className="pc-col">
            <span className="pc-col-title">Allocation</span>
            {allocRows.length === 0 ? (
              <span className="pc-empty">No positions</span>
            ) : (
              allocRows.map(r => (
                <div key={r.label} className="pc-alloc-row">
                  <span className={`pc-alloc-dot pc-dot--${r.cls}`} />
                  <span className="pc-alloc-label">{r.label}</span>
                  <div className="pc-alloc-bar-wrap">
                    <div
                      className={`pc-alloc-bar pc-bar--${r.cls}`}
                      style={{ width: `${base > 0 ? Math.min(100, (r.value / base) * 100) : 0}%` }}
                    />
                  </div>
                  <span className="pc-alloc-pct">
                    {base > 0 ? ((r.value / base) * 100).toFixed(0) : 0}%
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Unrealized P&L */}
          <div className="pc-col">
            <span className="pc-col-title">Unrealized P&amp;L</span>
            {totalCostBasis === 0 ? (
              <span className="pc-empty">No cost basis</span>
            ) : (
              <>
                <span className={`pc-pnl-amount ${totalPnl >= 0 ? 'pc-pnl--pos' : 'pc-pnl--neg'}`}>
                  {totalPnl >= 0 ? '+' : '−'}${Math.abs(totalPnl).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </span>
                <span className={`pc-pnl-pct ${totalPnlPct >= 0 ? 'pc-pnl--pos' : 'pc-pnl--neg'}`}>
                  {totalPnlPct >= 0 ? '+' : ''}{totalPnlPct.toFixed(1)}%
                </span>
                <span className="pc-pnl-sub">
                  Cost basis ${totalCostBasis.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </span>
              </>
            )}
          </div>

          {/* Stop status */}
          <div className="pc-col">
            <span className="pc-col-title">Stop Status</span>
            <div className="pc-status-list">
              <div className="pc-status-row">
                <span className="pc-status-dot pc-status-dot--green" />
                <span className="pc-status-num">{counts.GREEN}</span>
                <span className="pc-status-lbl">Green</span>
              </div>
              <div className="pc-status-row">
                <span className="pc-status-dot pc-status-dot--amber" />
                <span className="pc-status-num">{counts.AMBER}</span>
                <span className="pc-status-lbl">Amber</span>
              </div>
              <div className="pc-status-row">
                <span className="pc-status-dot pc-status-dot--red" />
                <span className="pc-status-num">{counts.RED}</span>
                <span className="pc-status-lbl">Red</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ══ Positions table ══ */}
      {positions.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📋</span>
          <p>No positions yet.<br />Click <strong>+ Add Position</strong> to get started.</p>
        </div>
      ) : (
        <div className="table-scroll">
          <table className="positions-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Class</th>
                <th>Shares</th>
                <th>Entry $</th>
                <th>Current $</th>
                <th>Stop $</th>
                <th>Cushion</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {positions.map(p => {
                const isCrypto      = p.assetClass === 'Crypto';
                const isStock       = p.assetClass === 'LETF' || p.assetClass === 'Stock';
                const isLive        = isCrypto || isStock;  // any position with a live feed
                const isOverridden  = manualOverrides[p.id] != null;
                const isEditingThis = editPrice?.id === p.id;
                const effPrice      = getEffectivePrice(p);
                const cushion       = calcCushion(effPrice, p.stopPrice);
                const status        = getStatus(cushion);
                const sl            = status.toLowerCase();
                // Suppress stale ⏰ for positions served by a live feed
                const stale         = !isLive && isPriceStale(p);
                const pnl           = (effPrice - (p.entryPrice ?? 0)) * p.shares;
                const pnlPct        = p.entryPrice > 0 ? ((effPrice - p.entryPrice) / p.entryPrice) * 100 : 0;
                const hasNote       = p.notes && p.notes.trim().length > 0;
                const noteOpen      = expandedNote === p.id;
                // Stock feed snapshot (null for non-stock positions)
                const sf            = isStock ? getStockData(p.name) : null;
                const sfBadge       = sf?.status ?? 'loading';
                const sfTime        = sf?.updatedAt
                  ? sf.updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : null;

                return (
                  <>
                    <tr key={p.id} className={`pos-row pos-row--${sl}`}>
                      <td className="td-name">
                        <div className="td-name-inner">
                          {stale && (
                            <span className="stale-icon" title={STALE_TIP} aria-label={STALE_TIP}>⏰</span>
                          )}
                          {p.name}
                          {hasNote && (
                            <button
                              className="note-toggle"
                              onClick={() => setExpandedNote(noteOpen ? null : p.id)}
                              aria-label="Toggle notes"
                              title={p.notes}
                            >📝</button>
                          )}
                        </div>
                      </td>
                      <td><span className={`class-tag class-tag--${p.assetClass.toLowerCase()}`}>{p.assetClass}</span></td>
                      <td>{p.shares}</td>
                      <td>${Number(p.entryPrice).toFixed(2)}</td>

                      {/* ── Current Price cell ── */}
                      <td className={stale ? 'td-stale' : ''}>
                        {isEditingThis ? (
                          /* Inline override input */
                          <div className="price-cell">
                            <input
                              className="price-override-input"
                              type="number"
                              min="0"
                              step="any"
                              value={editPrice.value}
                              onChange={e => setEditPrice({ id: p.id, value: e.target.value })}
                              onKeyDown={e => {
                                if (e.key === 'Enter')  commitOverride(p.id);
                                if (e.key === 'Escape') setEditPrice(null);
                              }}
                              onBlur={() => commitOverride(p.id)}
                              autoFocus
                            />
                          </div>
                        ) : (
                          <div className="price-cell">
                            <span>${effPrice.toFixed(2)}</span>

                            {/* ── Crypto: Live / Offline badge ── */}
                            {isCrypto && !isOverridden && (
                              <span className={`live-badge live-badge--${feedStatus}`}>
                                <span className="live-dot" />
                                {feedStatus === 'live' ? 'Live' : feedStatus === 'offline' ? 'Offline' : '…'}
                              </span>
                            )}

                            {/* ── Stock/LETF: Live / Offline badge + last-updated time ── */}
                            {isStock && !isOverridden && (
                              <span className={`live-badge live-badge--${sfBadge}`}>
                                <span className="live-dot" />
                                {sfBadge === 'live'
                                  ? (sfTime ? `Live · ${sfTime}` : 'Live')
                                  : sfBadge === 'offline' ? 'Offline' : '…'}
                              </span>
                            )}

                            {/* Resume Live — shown while any override is active */}
                            {isLive && isOverridden && (
                              <button
                                className="resume-live-btn"
                                onClick={() => resumeLive(p.id)}
                                title="Discard manual price and resume live feed"
                              >↺ Live</button>
                            )}

                            {/* Pencil — opens inline override input */}
                            {isLive && !isOverridden && (
                              <button
                                className="price-edit-btn"
                                onClick={() => setEditPrice({ id: p.id, value: String(effPrice.toFixed(2)) })}
                                title="Override price manually"
                              >✎</button>
                            )}
                          </div>
                        )}
                      </td>

                      <td>${Number(p.stopPrice).toFixed(2)}</td>
                      <td className={`td-cushion cushion--${sl}`}>{cushion.toFixed(1)}%</td>
                      <td>
                        <div className="td-status-pnl">
                          <span className={`status-pill status-pill--${sl}`}>{status}</span>
                          <span className={`td-pnl ${pnl >= 0 ? 'pnl--pos' : 'pnl--neg'}`}>
                            {pnl >= 0 ? '+' : '−'}${Math.abs(pnl).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                            <span className="td-pnl-pct">{pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%</span>
                          </span>
                        </div>
                      </td>
                      <td className="td-actions">
                        <button className="action-btn" onClick={() => setAddEditModal({ mode: 'edit', position: p })}>Edit</button>
                        {/* Pass effective price into TrailStopModal so cushion preview is accurate */}
                        <button
                          className="action-btn action-btn--trail"
                          onClick={() => setTrailModal({
                            ...p,
                            currentPrice: effPrice,
                            _liveStatus: isOverridden  ? 'manual'
                              : isCrypto   ? feedStatus
                              : isStock    ? sfBadge
                              : null,
                          })}
                        >Trail Stop</button>
                        <button className="action-btn action-btn--delete" onClick={() => handleDelete(p.id)} aria-label="Delete">✕</button>
                      </td>
                    </tr>
                    {noteOpen && (
                      <tr key={`${p.id}-note`} className="notes-row">
                        <td colSpan={9} className="notes-cell">
                          <div className="notes-content">
                            <span className="notes-label">📝 Notes</span>
                            <span className="notes-text">{p.notes}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ══ Modals ══ */}
      {addEditModal && (
        <PositionModal
          mode={addEditModal.mode}
          position={addEditModal.position}
          onSave={handleSavePosition}
          onClose={() => setAddEditModal(null)}
        />
      )}
      {trailModal && (
        <TrailStopModal
          position={trailModal}
          liveStatus={trailModal._liveStatus}
          onSave={handleTrailStop}
          onClose={() => setTrailModal(null)}
        />
      )}
      {showReview && <WeeklyReview onClose={() => setShowReview(false)} />}
    </div>
  );
}
